import React, { useState } from "react";
import axios from "axios";
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Label,
  Button,
  Form,
  Input,
  FormFeedback,
  Alert,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Breadcrumbs from "../../../Components/Common/Breadcrumb";
import { useFormik } from "formik";

const baseUrl = process.env.REACT_APP_BASE_URL;

// Define all feedback questions in a single JSON array
// Available response_type values: "text", "number", "textarea", "select", "rating"
const FEEDBACK_QUESTIONS = [
  {
    name: "overall_rating",
    question: "Overall Experience Rating",
    response_type: "rating",
    required: true,
    min: 1,
    max: 5,
  },
  {
    name: "service_type",
    question: "Which service did you use?",
    response_type: "select",
    required: true,
    options: ["Bookings", "Expert Consultation", "Company Formation", "HR Services", "Bookkeeping", "Tax Services", "Business Insurance", "Legal Services", "Government Contracting", "Web Development", "Other"],
  },
  {
    name: "often_use",
    question: "How Often Do You Use BizServe?",
    response_type: "select",
    required: true,
    options: ["Daily", "Weekly", "Monthly", "Yearly", "Other"],
  },
  {
    name: "experience_description",
    question: "Tell us about your experience",
    response_type: "textarea",
    required: true,
    placeholder: "Please share your thoughts...",
    rows: 4,
  },
  {
    name: "would_recommend",
    question: "Would you recommend BizServe to others?",
    response_type: "select",
    required: false,
    options: ["Yes, definitely", "Maybe", "No", "Not sure"],
  },
  {
    name: "improvement_suggestions",
    question: "How can we improve?",
    response_type: "textarea",
    required: false,
    placeholder: "Your suggestions are valuable to us...",
    rows: 4,
  },
];

const CustomerFeedback = () => {
  const [showloader, setshowloader] = useState(false);
  const [alertMsgg, setAlertMsgg] = useState<any>({
    open: false,
    type: "success",
    content: "Messages here.",
    time: "2500",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  document.title = "Feedback | BizServe";

  const toggleSuccessModal = () => {
    setSuccessModal(!successModal);
  };

  const handleAlertMsggClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMsgg({ ...alertMsgg, open: false });
  };

  // Generate initial values from questions JSON
  const generateInitialValues = () => {
    const initialValues: any = {};
    FEEDBACK_QUESTIONS.forEach((q) => {
      initialValues[q.name] = q.response_type === "rating" || q.response_type === "number" ? 0 : "";
    });
    return initialValues;
  };

  // Dynamic validation based on questions JSON
  const validate = (values: any) => {
    const errors: any = {};

    FEEDBACK_QUESTIONS.forEach((q) => {
      if (q.required) {
        if (q.response_type === "rating" || q.response_type === "number") {
          if (!values[q.name] || values[q.name] === 0) {
            errors[q.name] = `${q.question} is required`;
          }
        } else {
          if (!values[q.name] || values[q.name].trim() === "") {
            errors[q.name] = `${q.question} is required`;
          }
        }
      }
    });

    return errors;
  };

  // Initialize formik
  const formik = useFormik({
    initialValues: generateInitialValues(),
    validate,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);

        // Get user details from localStorage if available
        const userData = JSON.parse(localStorage.getItem("userdata") || "{}");

        // Convert form values to question-answer pairs
        const responsesArray = FEEDBACK_QUESTIONS.map((q) => ({
          question: q.question,
          answer: values[q.name] || "",
        }));

        const submissionData = {
          responses: responsesArray,
          customer_name: userData.username || "",
          customer_email: userData.email || "",
          customer_phone: userData.phone || "",
        };

        const response = await axios.post(
          `${baseUrl}/feedback/submit/`,
          submissionData,
          { withCredentials: true }
        );

        if (response.status === "SUBMITTED") {
          // Reset form completely
          formik.resetForm();
          formik.setValues(generateInitialValues());
          
          // Show success modal
          setSuccessModal(true);
        }

        setIsSubmitting(false);
      } catch (error: any) {
        console.error("Error submitting feedback:", error);
        setAlertMsgg({
          open: true,
          content:
            error.response?.data?.error || error.response?.data?.detail ||
            "Error submitting feedback. Please try again.",
          type: "danger",
          time: "3000",
        });
        setIsSubmitting(false);
      }
    },
  });

  // Render field based on response_type
  const renderField = (question: any) => {
    const { name, question: label, response_type, required, options, min, max, placeholder, rows } = question;

    const commonProps = {
      id: name,
      name: name,
      value: formik.values[name] || (response_type === "rating" || response_type === "number" ? 0 : ""),
      onChange: formik.handleChange,
      onBlur: formik.handleBlur,
      invalid: !!(formik.touched[name] && formik.errors[name]),
    };

    switch (response_type) {
      case "rating":
        return (
          <div className="d-flex gap-2">
            {[1, 2, 3, 4, 5].map((rating) => (
              <Button
                key={rating}
                type="button"
                color={formik.values[name] >= rating ? "warning" : "light"}
                onClick={() => formik.setFieldValue(name, rating)}
                className="btn-sm"
              >
                ★ {rating}
              </Button>
            ))}
          </div>
        );

      case "number":
        return (
          <Input
            {...commonProps}
            type="number"
            min={min}
            max={max}
            placeholder={placeholder}
          />
        );

      case "textarea":
        return (
          <Input
            {...commonProps}
            type="textarea"
            rows={rows || 4}
            placeholder={placeholder}
          />
        );

      case "select":
        return (
          <Input {...commonProps} type="select">
            <option value="">Select an option</option>
            {options?.map((option: string, idx: number) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </Input>
        );

      case "text":
      default:
        return (
          <Input
            {...commonProps}
            type="text"
            placeholder={placeholder}
          />
        );
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Customer" breadcrumbItem="Feedback" />

          {alertMsgg.open && (
            <Alert
              color={alertMsgg.type}
              isOpen={alertMsgg.open}
              toggle={handleAlertMsggClose}
            >
              {alertMsgg.content}
            </Alert>
          )}

          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <CardTitle className="mb-4">
                    Share Your Experience
                  </CardTitle>
                  <p className="text-muted mb-4">
                    We value your feedback! Help us improve by sharing your experience with BizServe.
                  </p>

                  <Form onSubmit={formik.handleSubmit}>
                    {/* Dynamically render all questions from JSON */}
                    {FEEDBACK_QUESTIONS.map((question, index) => (
                      <FormGroup key={index}>
                        <Label htmlFor={question.name}>
                          {question.question}
                          {question.required && (
                            <span className="text-danger"> *</span>
                          )}
                        </Label>
                        {renderField(question)}
                        {formik.touched[question.name] &&
                          formik.errors[question.name] && (
                            <FormFeedback type="invalid" className="d-block">
                              {String(formik.errors[question.name])}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    ))}

                    <div className="d-flex gap-2 mt-4">
                      <Button
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Submitting..." : "Submit Feedback"}
                      </Button>
                      <Button
                        color="light"
                        type="button"
                        onClick={() => formik.resetForm()}
                      >
                        Reset
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Success Modal */}
      <Modal isOpen={successModal} toggle={toggleSuccessModal} centered>
        <ModalHeader toggle={toggleSuccessModal}>
          Feedback Submitted Successfully!
        </ModalHeader>
        <ModalBody>
          <div className="text-center py-3">
            <i className="bx bx-check-circle text-success" style={{ fontSize: "72px" }}></i>
            <h4 className="mt-3">Thank You!</h4>
            <p className="text-muted">
              Your feedback has been submitted successfully. We appreciate your input and will use it to improve our services.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggleSuccessModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default CustomerFeedback;
